
import React from 'react';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LazyImage } from '@/components/ui/lazy-image';
import { ExternalLink } from 'lucide-react';
import { useProductClicks } from '@/hooks/product/useProductClicks';
import { useIsMobile } from '@/hooks/use-mobile';

interface Product {
  id: string;
  name: string;
  description?: string;
  image_url?: string;
  affiliate_link: string;
  category?: string;
  price?: number;
  currency?: string;
  source: 'recommendations';
  packing_list_title?: string;
  packing_list_destination?: string;
  created_at?: string; // Added to make it compatible with useProductClicks
}

interface ProductRecommendationCardProps {
  product: Product;
}

export function ProductRecommendationCard({ product }: ProductRecommendationCardProps) {
  const { trackProductClick } = useProductClicks();
  const isMobile = useIsMobile();

  const handleViewProductClick = () => {
    // Create a compatible product object with all required fields
    const trackableProduct = {
      ...product,
      created_at: product.created_at || new Date().toISOString(),
    };
    
    trackProductClick(trackableProduct);
    window.open(product.affiliate_link, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card className="overflow-hidden group h-full flex flex-col hover:shadow-md transition-shadow duration-300">
      {product.image_url ? (
        <div className="aspect-video relative overflow-hidden">
          <LazyImage 
            src={product.image_url} 
            alt={product.name} 
            className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
            blurUpPreview={true}
          />
        </div>
      ) : (
        <div className="aspect-video bg-secondary flex items-center justify-center">
          <span className="text-muted-foreground">No image</span>
        </div>
      )}
      
      <CardContent className="p-4 flex-1">
        <h3 className="text-lg font-semibold mb-2 line-clamp-2">{product.name}</h3>
        
        {product.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
            {product.description}
          </p>
        )}
        
        {product.category && (
          <div className="text-xs text-muted-foreground mb-2">
            Category: {product.category}
          </div>
        )}
        
        {product.price && (
          <div className="text-sm font-medium">
            ${product.price} {product.currency || 'USD'}
          </div>
        )}
      </CardContent>
      
      <CardFooter className="px-4 pb-4 pt-0">
        <Button 
          className="w-full gap-1.5"
          size={isMobile ? "sm" : "default"}
          onClick={handleViewProductClick}
          variant="default"
        >
          <span className={isMobile ? "text-xs" : "text-sm"}>View Product</span>
          <ExternalLink className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
}
